/**
 * EngagementBarCompact — Fixed 4-icon bar for ALL cards on home screen
 * 
 * Layout: FoodList (count) · Post FoodCard (count) · Google Reviews (star+G) · Share
 * Height: ~48px, full-width, thin top divider
 * Icons: 24px, outline gray inactive, orange on tap
 * No labels — counts only for first two
 */
import { useState, useCallback } from 'react';
import { Camera, Share2, Layers, Star, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import FoodCardIcon from './FoodCardIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GoogleGIcon = ({ active }) => (
  <span className="relative inline-flex items-center justify-center w-4 h-4">
    <Star className={`w-4 h-4 ${active ? 'fill-amber-400 text-amber-400' : 'text-gray-400'}`} strokeWidth={1.5} />
    <span className={`absolute text-[5px] font-black leading-none ${active ? 'text-white' : 'text-gray-400'}`} style={{ top: '5px' }}>G</span>
  </span>
);

const EngagementBarCompact = ({
  itemName = '',
  itemId = '',
  placeId = null,
  rating = null,
  foodlistCount = 0,
  foodcardCount = 0,
  onFoodlistTap,
  isFoodlisted = false,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saveAnim, setSaveAnim] = useState(false);
  const [cardAnim, setCardAnim] = useState(false);
  const [localFoodlistCount, setLocalFoodlistCount] = useState(foodlistCount);
  const [showReviews, setShowReviews] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const handleFoodlist = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFoodlistTap) onFoodlistTap();
    if (!isFoodlisted) {
      setSaveAnim(true);
      setLocalFoodlistCount(c => c + 1);
      setTimeout(() => setSaveAnim(false), 800);
    } else {
      setLocalFoodlistCount(c => Math.max(0, c - 1));
    }
  }, [onFoodlistTap, isFoodlisted]);

  const handlePostFoodCard = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCardAnim(true);
    setTimeout(() => setCardAnim(false), 800);
    if (user) navigate('/create-foodcard', { state: { prefill: { name: itemName } } });
  }, [user, navigate, itemName]);

  const handleGoogleReviews = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowReviews(prev => !prev);
    if (reviews || !placeId) return;
    setReviewsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/places/details/${placeId}`);
      if (res.ok) setReviews(await res.json());
    } catch {} finally { setReviewsLoading(false); }
  }, [placeId, reviews]);

  const handleShare = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = `Check out ${itemName} on FoodQuest!`;
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: itemName, text, url }); } catch {}
    } else {
      try {
        await navigator.clipboard.writeText(`${text}\n${url}`);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = `${text}\n${url}`;
        ta.style.cssText = 'position:fixed;left:-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
    }
  }, [itemName]);

  return (
    <div className="relative">
      {/* Bar — 2 rows: Icon top, Count+Label bottom */}
      <div className="flex items-start justify-between px-2 py-1.5 border-t border-gray-100" onClick={(e) => e.preventDefault()}>
        <button onClick={handleFoodlist} className={`relative flex flex-col items-center gap-0.5 flex-1 p-1 rounded-md transition-all ${isFoodlisted ? 'text-[#FF6B35]' : 'text-gray-400 hover:text-gray-600'}`} data-testid={`bar-foodlist-${itemId}`}>
          <Layers className={`w-6 h-6 ${isFoodlisted ? 'fill-current' : ''}`} strokeWidth={1.5} />
          <span className={`text-[8px] font-bold leading-tight transition-all ${saveAnim ? 'text-[#FF6B35] scale-110' : ''}`}>{localFoodlistCount || 0} <span className="font-medium">FoodList</span></span>
          {saveAnim && <span className="absolute -top-1 right-0 text-[7px] text-[#FF6B35] font-bold animate-bounce">+1</span>}
        </button>
        <button onClick={handlePostFoodCard} className="relative flex flex-col items-center gap-0.5 flex-1 p-1 rounded-md transition-all text-gray-400 hover:text-gray-600" data-testid={`bar-foodcard-${itemId}`}>
          <FoodCardIcon size={28} className="text-[#FF6B35]" />
          <span className={`text-[8px] font-bold leading-tight transition-all ${cardAnim ? 'text-[#FF6B35] scale-110' : ''}`}>{foodcardCount || 0} <span className="font-medium">FoodCard</span></span>
          {cardAnim && <span className="absolute -top-1 right-0 text-[7px] text-[#FF6B35] font-bold animate-bounce">+1</span>}
        </button>
        <button onClick={handleGoogleReviews} className={`flex flex-col items-center gap-0.5 flex-1 p-1 rounded-md transition-all ${showReviews ? 'text-amber-500' : 'text-gray-400 hover:text-gray-600'}`} data-testid={`bar-greviews-${itemId}`}>
          <GoogleGIcon active={showReviews} />
          <span className="text-[8px] font-bold leading-tight">{rating || '4.0'} <span className="font-medium">Reviews</span></span>
        </button>
        <button onClick={handleShare} className="flex flex-col items-center gap-0.5 flex-1 p-1 rounded-md transition-all text-gray-400 hover:text-gray-600" data-testid={`bar-share-${itemId}`}>
          <Share2 className="w-4 h-4" strokeWidth={1.5} />
          <span className="text-[8px] font-medium leading-tight">Share</span>
        </button>
      </div>

      {/* Google Reviews popover */}
      {showReviews && (
        <div className="px-2.5 pb-2" onClick={(e) => e.preventDefault()}>
          <div className="bg-amber-50/60 rounded-lg p-2">
            {reviewsLoading ? (
              <div className="flex items-center gap-2 text-xs text-gray-500"><div className="w-3 h-3 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" /> Loading...</div>
            ) : reviews ? (
              <>
                <div className="flex items-center gap-1.5 mb-2">
                  {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(reviews.rating || rating || 4) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />)}
                  <span className="text-xs font-bold text-gray-900 ml-1">{reviews.rating}</span>
                  <span className="text-[10px] text-gray-400">({reviews.user_ratings_total})</span>
                </div>
                {reviews.reviews?.slice(0, 1).map((r, i) => (
                  <p key={i} className="text-[10px] text-gray-600 line-clamp-2 mb-1">"{r.text}"</p>
                ))}
                <a href={`https://www.google.com/maps/place/?q=place_id:${placeId}`} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blue-600 font-medium hover:underline flex items-center gap-0.5">
                  Review on Google <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </>
            ) : (
              <div className="flex items-center gap-1.5">
                {[1,2,3,4,5].map(s => <Star key={s} className={`w-3.5 h-3.5 ${s <= Math.round(rating || 4) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />)}
                <span className="text-xs font-bold text-gray-900 ml-1">{rating || '4.0'}</span>
                <a href={`https://www.google.com/search?q=${encodeURIComponent(itemName + ' reviews')}`} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-blue-600 font-medium ml-auto hover:underline">Google Reviews</a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementBarCompact;
